import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Plus, X, Save } from 'lucide-react';

const PRESET_SOURCES = [
  'Facebook Ads','Google Ads','TikTok','Instagram Ads','YouTube Ads',
  'TV','Billboard','Radio','CTV','Display Ads','Bing Ads',
  'Doctor Referral','Patient Referral','Dealership Consult',
  'Rebooking','Other Referral','Other'
];
const PRESET_PAYMENT = [
  'CareCredit','Cherry','Lending Club','PatientFi','Proceede',
  'Sunbit','Wisetack','Alphaeon','In House Financing',
  'Cash / Check / Credit Card','Other'
];
const PRESET_TREATMENTS = [
  'Double Full Arch','Full Arch Upper','Full Arch Lower',
  'Single Implant','Multiple Implants','All-on-4','All-on-6',
  'Implant Supported Denture','Bone Graft','Sinus Lift','Other'
];

const SectionEditor = ({ title, description, presets, current, onChange }) => {
  const [customInput, setCustomInput] = useState('');

  const toggle = (item) => {
    if (current.includes(item)) onChange(current.filter(i => i !== item));
    else onChange([...current, item]);
  };

  const addCustom = () => {
    const v = customInput.trim();
    if (!v) return;
    if (current.includes(v)) { toast.error('Already in list'); return; }
    onChange([...current, v]);
    setCustomInput('');
  };

  return (
    <Card className="bg-white shadow-sm">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-heading">{title}</CardTitle>
        {description && <p className="text-xs text-slate-400 mt-0.5">{description}</p>}
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Quick-select chips */}
        <div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-2.5">Quick Select — click to add/remove</p>
          <div className="flex flex-wrap gap-2">
            {presets.map(item => {
              const active = current.includes(item);
              return (
                <button key={item} onClick={() => toggle(item)}
                  className={`text-xs px-3 py-1.5 rounded-full border font-medium transition-all select-none ${
                    active ? 'bg-blue-600 text-white border-blue-600 shadow-sm scale-[1.02]'
                           : 'bg-white text-slate-600 border-slate-200 hover:border-blue-400 hover:text-blue-700'
                  }`}>
                  {active && '✓ '}{item}
                </button>
              );
            })}
          </div>
        </div>

        {/* Active list (shows custom + preset-selected) */}
        {current.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-2">Active ({current.length} selected)</p>
            <div className="flex flex-wrap gap-1.5">
              {current.map(item => (
                <Badge key={item} className="gap-1 pl-2.5 pr-1.5 py-1 text-xs bg-slate-100 text-slate-700 border-0">
                  {item}
                  <button onClick={() => onChange(current.filter(i => i !== item))} className="hover:text-red-500">
                    <X size={10} />
                  </button>
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Custom input */}
        <div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-2">Add Custom</p>
          <div className="flex gap-2">
            <Input value={customInput} onChange={e => setCustomInput(e.target.value)}
              placeholder={`Type to add a custom ${title.toLowerCase()} option...`}
              className="h-9 text-sm"
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }} />
            <Button size="sm" variant="outline" className="h-9 gap-1 px-3" onClick={addCustom}>
              <Plus size={13} /> Add
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const StaffSection = ({ configKey, title, desc, placeholder, config, update }) => {
  const [inp, setInp] = useState('');
  const items = config[configKey] || [];
  const add = () => { const v = inp.trim(); if (!v || items.includes(v)) return; update(configKey, [...items, v]); setInp(''); };
  return (
    <Card className="bg-white shadow-sm">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-heading">{title}</CardTitle>
        <p className="text-xs text-slate-400">{desc}</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Input value={inp} onChange={e => setInp(e.target.value)} placeholder={placeholder}
            className="h-8 text-sm" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} />
          <Button size="sm" variant="outline" className="h-8" onClick={add}><Plus size={13} /></Button>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {items.map(item => (
            <Badge key={item} className="gap-1 pl-2.5 pr-1.5 py-1 text-xs bg-slate-100 text-slate-700 border-0">
              {item}
              <button onClick={() => update(configKey, items.filter(i => i !== item))} className="hover:text-red-500"><X size={10}/></button>
            </Badge>
          ))}
          {!items.length && <p className="text-xs text-slate-400">No staff added yet</p>}
        </div>
      </CardContent>
    </Card>
  );
};

export default function AdminDropdownsPage() {
  const [config, setConfig] = useState({
    payment_methods: [], referral_sources: [], treatment_types: [],
    sales_staff: [], eod_staff: [], call_center_staff: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/dropdowns`).then(r => setConfig(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const update = (key, items) => setConfig(prev => ({ ...prev, [key]: items }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/dropdowns`, config);
      toast.success('Dropdown settings saved!');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-slate-400 text-sm p-4">Loading...</div>;

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-xl font-bold text-slate-900 tracking-tight">Dropdown Settings</h1>
          <p className="text-slate-500 text-sm mt-1">Click chips to quickly add presets, or type to add custom options</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="gap-2 font-semibold" data-testid="admin-dropdowns-save-button">
          <Save size={14} /> {saving ? 'Saving...' : 'Save All'}
        </Button>
      </div>

      <Tabs defaultValue="sources">
        <TabsList className="bg-slate-100 border border-slate-200 mb-6 p-1 rounded-xl flex-wrap h-auto gap-1">
          {[['sources','Referral Sources'],['payment','Financing & Payment'],['treatments','Treatments'],['staff','Staff']].map(([v,l]) => (
            <TabsTrigger key={v} value={v} className="data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs rounded-lg px-3 py-1.5">{l}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="sources">
          <SectionEditor title="Referral Sources"
            description="Where patients come from. Click to add from common sources, or type to add your own."
            presets={PRESET_SOURCES} current={config.referral_sources || []} onChange={v => update('referral_sources', v)} />
        </TabsContent>
        <TabsContent value="payment">
          <SectionEditor title="Financing & Payment Methods"
            description="Financing companies and payment methods your patients use."
            presets={PRESET_PAYMENT} current={config.payment_methods || []} onChange={v => update('payment_methods', v)} />
        </TabsContent>
        <TabsContent value="treatments">
          <SectionEditor title="Treatment Types"
            description="Treatment options offered at your practice."
            presets={PRESET_TREATMENTS} current={config.treatment_types || []} onChange={v => update('treatment_types', v)} />
        </TabsContent>
        <TabsContent value="staff" className="space-y-4">
          <StaffSection configKey="sales_staff" title="Sales Staff (Closed By)" config={config} update={update}
            desc='Shown as "Closed By" on Sale Entry and EOD Sales forms' placeholder="e.g. Dr. Jane Smith" />
          <StaffSection configKey="eod_staff" title="EOD Staff — Sales" config={config} update={update}
            desc='Shown as "Submitted By" on EOD Summary (Sales) form' placeholder="e.g. Office Manager" />
          <StaffSection configKey="call_center_staff" title="Call Center Staff" config={config} update={update}
            desc='Shown as "Submitted By" on EOD Summary (Call Center) form' placeholder="e.g. CC Agent" />
        </TabsContent>
      </Tabs>

      <div className="pb-8">
        <Button onClick={handleSave} disabled={saving} className="w-full h-10 font-semibold gap-2">
          <Save size={14} /> {saving ? 'Saving...' : 'Save All Settings'}
        </Button>
      </div>
    </div>
  );
}
